import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, ChevronRight, ClipboardCheck, Heart, Dumbbell, Utensils, Brain } from 'lucide-react';

// Maps a journey "today_breakdown" key → a user-facing mission + where it goes.
const MISSION_META = {
  checkin: { label: 'Do your daily check-in', icon: Heart,    route: '/dashboard' },
  focus:   { label: 'Complete a workout',     icon: Dumbbell, route: '/workouts' },
  mind:    { label: 'Do a mind session',      icon: Brain,    route: '/mental' },
  meals:   { label: 'Log a healthy meal',     icon: Utensils, route: '/nutrition' },
};
const ORDER = ['focus', 'mind', 'meals', 'checkin'];

/**
 * TodaysMission — a small daily checklist on the Living dashboard. Pure view over
 * the journey "today_breakdown" the app already computes (no new data source).
 */
const TodaysMission = ({ breakdown }) => {
  const navigate = useNavigate();
  if (!breakdown || breakdown.length === 0) return null;

  const byKey = Object.fromEntries(breakdown.map((b) => [b.key, b]));
  const missions = ORDER.map((k) => ({ key: k, ...MISSION_META[k], done: !!byKey[k]?.done }))
    .filter((m) => m.label);
  const doneCount = missions.filter((m) => m.done).length;

  return (
    <div className="hpme-card p-5 mt-3 lg:mt-5" data-testid="todays-mission">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <ClipboardCheck className="w-4 h-4 text-primary" />
          <h3 className="font-secondary font-bold text-base tracking-tight text-white">Today's Mission</h3>
        </div>
        <span className="text-[11px] font-bold text-primary tabular-nums" data-testid="mission-progress">
          {doneCount}/{missions.length}
        </span>
      </div>

      <div className="space-y-2">
        {missions.map((m) => {
          const Icon = m.icon;
          return (
            <button
              key={m.key}
              type="button"
              onClick={() => !m.done && navigate(m.route)}
              data-testid={`mission-${m.key}`}
              className={`w-full flex items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition-colors ${
                m.done
                  ? 'border-primary/30 bg-primary/[0.06] cursor-default'
                  : 'border-white/10 bg-white/[0.02] hover:border-white/25 active:scale-[0.99]'
              }`}
            >
              <span
                className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${
                  m.done ? 'bg-primary text-black' : 'bg-zinc-800 text-zinc-500'
                }`}
              >
                {m.done ? <Check className="w-3.5 h-3.5" /> : <Icon className="w-3.5 h-3.5" />}
              </span>
              <span className={`flex-1 text-sm ${m.done ? 'text-zinc-300 line-through decoration-primary/40' : 'text-zinc-100'}`}>
                {m.label}
              </span>
              {!m.done && <ChevronRight className="w-4 h-4 text-zinc-500 shrink-0" />}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default TodaysMission;
